/**
 * Updates List - Display EAS updates in a table
 */

import { ReactTable } from "@aix/ui"

interface UpdatesListProps {
  updateGroups: any[]
  isLoading: boolean
  projectSlug: string
}

export function UpdatesList({ updateGroups, isLoading, projectSlug }: UpdatesListProps) {
  if (isLoading) {
    return <div className="text-muted-foreground">Loading updates...</div>
  }

  if (updateGroups.length === 0) {
    return <div className="text-muted-foreground">No updates found for this channel</div>
  }

  // Flatten update groups to individual updates for table
  const updates = updateGroups.flatMap((group) =>
    group.updates.map((update: any) => ({
      ...update,
      groupId: group.id,
      groupRuntimeVersion: group.runtimeVersion,
    })),
  )

  const columns = [
    {
      Header: "Message",
      accessor: "message",
      Cell: ({ row }: any) => (
        <div className="max-w-md">
          <p className="font-medium text-foreground truncate">{row.message}</p>
          <p className="text-xs text-muted-foreground mt-1">ID: {row.id}</p>
        </div>
      ),
    },
    {
      Header: "Platform",
      accessor: "platform",
      Cell: ({ row }: any) => (
        <span className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded">
          {row.platform === "ios" ? "📱 iOS" : "🤖 Android"}
        </span>
      ),
    },
    {
      Header: "Runtime Version",
      accessor: "runtimeVersion",
      Cell: ({ row }: any) => (
        <span className="text-xs font-mono text-muted-foreground">{row.runtimeVersion?.substring(0, 12)}...</span>
      ),
    },
    {
      Header: "Deployed By",
      accessor: "actor",
      Cell: ({ row }: any) => {
        const actor = row.actor
        if (!actor) return <span className="text-muted-foreground">—</span>
        const name =
          actor.firstName && actor.lastName ? `${actor.firstName} ${actor.lastName}` : actor.username || "Unknown"
        return <span className="text-sm text-foreground">{name}</span>
      },
    },
    {
      Header: "Created",
      accessor: "createdAt",
      Cell: ({ row }: any) => {
        const date = new Date(row.createdAt)
        return (
          <div className="text-sm">
            <p className="text-foreground">{date.toLocaleDateString()}</p>
            <p className="text-xs text-muted-foreground">{date.toLocaleTimeString()}</p>
          </div>
        )
      },
    },
    {
      Header: "Actions",
      accessor: "id",
      Cell: ({ row }: any) => (
        <div className="flex gap-2">
          <button
            onClick={() => {
              // TODO: Navigate to update details
              console.log("View update:", row.id)
            }}
            className="text-xs text-primary hover:underline"
          >
            View
          </button>
          {row.manifestPermalink && (
            <a
              href={row.manifestPermalink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline"
            >
              Manifest
            </a>
          )}
        </div>
      ),
    },
  ]

  return (
    <div>
      <ReactTable data={updates} columns={columns} />
    </div>
  )
}
